import { type PropsWithChildren } from 'react';

import {
  container,
  tooltipOverlay,
  tooltipOverlayFull,
  consumerLoadingStyle,
} from './GaugeLayoutOverlay.sty.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { LoaderOverlay } from '../../../core/components/loader-overlay/LoaderOverlay.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useGaugeChartOverlayEvents } from '../../hooks/useGaugeChartOverlayEvents.js';
import { useGaugeChartTooltipOverlayBounds } from '../../hooks/useGaugeChartTooltipOverlayBounds.js';
import {
  GaugeChartToolbar,
  type GaugeChartToolbarProps,
} from '../GaugeChartLayout/GaugeChartToolbar.js';
import { GaugeOverlayTooltip } from '../GaugeChartTooltip/GaugeOverlayTooltip.js';

interface GaugeChartOverlayProps {
  value: number;
  color: string;
  showLoading?: boolean;
  intents: GaugeChartToolbarProps['intents'];
  toolbar: GaugeChartToolbarProps['toolbar'];
}

export const GaugeChartOverlay = (
  props: PropsWithChildren<GaugeChartOverlayProps>,
) => {
  const { children, value, color, intents, toolbar, showLoading } = props;
  const overlay = useOverlay();
  const overlayBounds = useGaugeChartTooltipOverlayBounds();
  const { onPointerMove, onClick, onPointerLeave } = useGaugeChartOverlayEvents(
    {
      value,
      color,
    },
  );
  const overlayClassName = overlayBounds
    ? tooltipOverlay
    : `${tooltipOverlay} ${tooltipOverlayFull}`;
  const overlayStyle = overlayBounds
    ? {
        top: overlayBounds.top,
        left: overlayBounds.left,
        width: overlayBounds.width,
        height: overlayBounds.height,
      }
    : undefined;

  return (
    <Flex
      ref={overlay.overlayRef}
      data-testid="gauge-overlay-wrapper"
      className={container({ showLoading })}
    >
      <GaugeChartToolbar intents={intents} toolbar={toolbar} />

      {!showLoading && (
        <Flex
          className={overlayClassName}
          onPointerMove={onPointerMove}
          onPointerLeave={onPointerLeave}
          onClick={onClick}
          style={overlayStyle}
        >
          <GaugeOverlayTooltip value={value} color={color} />
        </Flex>
      )}

      {children}
      {showLoading ? (
        <LoaderOverlay
          gridArea="1/1/2/2"
          consumerStyle={consumerLoadingStyle}
        />
      ) : null}
    </Flex>
  );
};

GaugeChartOverlay['displayName'] = 'GaugeChartOverlay';
